import i18n from '@/i18n';
import { parseIntakeFormDate } from './IntakeFormDateUtil';
import { getParticipantEndTimeSyncResult, validateTimeDurationRange } from './IntakeFormTimeUtil';
import { isNormalisablePhone } from './phone';
import { isEmailAddress } from './emailAddress';

const hasRequiredValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return value !== null && value !== undefined && value !== '';
};

export const formRules = {
  text_field_rules: {
    required: (value) => hasRequiredValue(value) || content.required_field,
    isNumber: (value) => !isNaN(value) || content.is_number,
    isYear: (value) => {
      value = typeof value === 'number' ? value.toString() : value;
      const currentYear = new Date().getFullYear();
      return (value.length === 4 && /^\d+$/.test(value) && value <= currentYear && value !== '0000') || content.is_year;
    },
    IsPositive: (value) => value >= 0 || content.is_positive,
    // Dezelfde grens als de server (util/emailAddress): een adres dat hier
    // doorkomt, weigert Laravel niet met een kale 400.
    isEmail: (value) => !value || isEmailAddress(value) || content.is_email,
    // Het telefoonnummer van een deelnemer, uit elk land (ADR-0099): de waarde
    // die components/phone-field.vue doorgeeft, of een opgeslagen E.164. Leeg
    // is geen invoerfout -- of het veld verplicht is, beslist het scherm.
    isPhone: (value) => isNormalisablePhone(value) || content.is_phone,
    // NL-focused: +31 / 0031 / 0 + mobile (6xxxxxxxx) or landline ([1-9]xxxxxxxx).
    // Alleen nog voor het telefoonveld in een intakeformulier (PhoneInput.vue);
    // het nummer van een deelnemer gaat door isPhone.
    isNlPhone: (value) => {
      const cleaned = String(value ?? '')
        .trim()
        .replace(/[\s\-().]/g, '');
      // Leeg is geen invoerfout: of het veld verplicht is, beslist het
      // formulier. Een veld met alleen spaties telt hier als leeg.
      if (!cleaned) return true;
      // De `0?` na de landcode vangt `+31 (0)6…`, de schrijfwijze van elk
      // visitekaartje.
      const nlRegex = /^(?:(?:\+31|0031)0?|0)(?:6\d{8}|[1-9]\d{8})$/;
      return nlRegex.test(cleaned) || content.is_phone_nl;
    },
    isLink: (value) => {
      if (!value) return true;

      const link = String(value).trim();
      if (!link || /[\s<>]/.test(link)) return content.is_link;

      if (/^[a-z][a-z0-9+.-]*:/i.test(link) && !/^https?:/i.test(link)) {
        return content.is_link;
      }
      const absoluteUrlRegex = /^https?:\/\/[^\s/?#]+\.[^\s/?#]+(?:[/?#].*)?$/i;
      const domainUrlRegex = /^(?:www\.)?[^\s/?#]+\.[^\s/?#]+(?:[/?#].*)?$/i;
      const relativeUrlRegex = /^\/[^\s<>]*$/;

      return (
        absoluteUrlRegex.test(link) ||
        domainUrlRegex.test(link) ||
        relativeUrlRegex.test(link)
      ) || content.is_link;
    },
    maxLength: (max) => (value) => (value?.length ?? 0) <= max || content.max_length.replace('{max}', max),
    minLength: (min) => (value) => (value?.length ?? 0) >= min || content.min_length.replace('{min}', min),
    // The Verwacht aantal deelnemers counterpart of checkDuration. The count the
    // user typed is always kept; what this refuses is the Afgeleide wijziging that
    // would follow it, which is why the copy names the eindtijd that was not
    // written (ADR-0025). Being a rule, it re-evaluates when the venster or the
    // Sessieduur changes, so correcting the starttijd clears it on its own.
    checkParticipantDuration: (settings, times, sessionDuration) => (value) => {
      const result = getParticipantEndTimeSyncResult(
        times?.start_time ?? null,
        sessionDuration,
        value,
        settings,
      );
      const reason = result?.validation?.reason;

      if (!reason) {
        return true;
      }

      return i18n.global.t(`intake_time_sync.participants_duration_error.${reason}`);
    },
  },
  radio_group_rules: {
    required: (value) => hasRequiredValue(value) || content.required_field,
  },
  select_rules: {
    required: (value) => hasRequiredValue(value) || content.select_required,
    minItems: (value) => (Array.isArray(value) && value.length >= 2) || content.min_items,
  },
  date_input_rules: {
    // Accepts Date instances, intake form dates (dd-MM-yyyy), or native date strings.
    required: (value) => hasRequiredValue(value) || content.required_field,
    isValidDate: (value) => {
      if (!hasRequiredValue(value)) return true;

      const date = parseToDate(value);
      return (date instanceof Date && !isNaN(date.getTime())) || content.invalid_date;
    },
    minDate: (min) => (value) => {
      const date = parseToDate(value);
      const minDate = parseToDate(min);
      if (!(date instanceof Date) || isNaN(date)) return content.invalid_date;
      if (!(minDate instanceof Date) || isNaN(minDate)) return true;
      return date >= stripTime(minDate) || content.min_date.replace('{min}', formatYmd(minDate));
    },
    maxDate: (max) => (value) => {
      const date = parseToDate(value);
      const maxDate = parseToDate(max);
      if (!(date instanceof Date) || isNaN(date)) return content.invalid_date;
      if (!(maxDate instanceof Date) || isNaN(maxDate)) return true;
      return date <= endOfDay(maxDate) || content.max_date.replace('{max}', formatYmd(maxDate));
    },
    isPast: (value) => {
      const date = parseToDate(value);
      if (!(date instanceof Date) || isNaN(date)) return content.invalid_date;
      const now = new Date();
      return date <= now || content.past_date;
    },
    isAdult: (years = 18) => (value) => {
      const date = parseToDate(value);
      if (!(date instanceof Date) || isNaN(date)) return content.invalid_date;
      const threshold = new Date();
      threshold.setFullYear(threshold.getFullYear() - years);
      return date <= threshold || content.adult_date.replace('{years}', years);
    },
    minHoursFromNowEpochStart: (cutoffHours = 0, startTime = "00:00", startDateEpoch) => () => {
      if (!startDateEpoch) return content.invalid_date;

      // if epoch is in seconds, convert to ms
      const epochMs = startDateEpoch < 1e12 ? startDateEpoch * 1000 : startDateEpoch;

      const dt = combineEpochDateAndTime(epochMs, startTime);
      if (!(dt instanceof Date) || Number.isNaN(dt.getTime())) return content.invalid_date;

      const h = Number(cutoffHours);
      if (Number.isNaN(h) || h < 0) return true;

      const threshold = new Date(Date.now() + h * 60 * 60 * 1000);
      return dt >= threshold || i18n.global.t('form_rules.min_hours_from_now', { hours: h });
    },
    checkDuration: (minDuration, maxDuration, times) => () => {
      if (!times) return true;

      const result = validateTimeDurationRange(times, {
        min_duration: minDuration,
        max_duration: maxDuration,
      });

      if (result.reason === 'min') {
        return i18n.global.t('form_rules.min_duration_rule', { min: Number(minDuration) });
      }
      if (result.reason === 'max') {
        return i18n.global.t('form_rules.max_duration_rule', { max: Number(maxDuration) });
      }
      if (result.reason === 'range') {
        return i18n.global.t('form_rules.time_range_rule');
      }
      return true;
    },
  },
  priceEndRule(start, end) {
    const priceStart = parseFloat(start);
    const priceEnd = parseFloat(end);
    if (isNaN(priceStart) || isNaN(priceEnd)) {
      return true; // The required rule will handle empty fields
    }
    return priceEnd >= priceStart || content.price_end_rule;
  },
  checkbox_rules: {
    required: (value) => {
      if (Array.isArray(value)) return value.length > 0 || content.checkbox_required;
      return value === true || content.checkbox_required;
    },
  },
  file_upload_rules: {
    required: (value) => {
      if (!value) return content.file_required;
      // Handle both single File object and array of File objects
      if (value instanceof File) return true;
      if (Array.isArray(value)) return value.length > 0 || content.file_required;
      return content.file_required;
    },
    maxSize: (maxSizeInMB) => (value) => {
      if (!value) return true; // Let required rule handle empty files
      const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
      // Handle both single File object and array of File objects
      if (value instanceof File) {
        return value.size <= maxSizeInBytes || content.file_max_size.replace('{max}', maxSizeInMB);
      }
      if (Array.isArray(value)) {
        if (value.length === 0) return true;
        return value.every(file => file.size <= maxSizeInBytes) || content.file_max_size.replace('{max}', maxSizeInMB);
      }
      return true;
    },
    allowedTypes: (allowedTypes) => (value) => {
      if (!value) return true; // Let required rule handle empty files
      // Handle both single File object and array of File objects
      if (value instanceof File) {
        return allowedTypes.some(type => value.type.includes(type)) || content.file_allowed_types.replace('{types}', allowedTypes.join(', '));
      }
      if (Array.isArray(value)) {
        if (value.length === 0) return true;
        return value.every(file => allowedTypes.some(type => file.type.includes(type))) || content.file_allowed_types.replace('{types}', allowedTypes.join(', '));
      }
      return true;
    },
    maxFiles: (maxCount) => (value) => {
      if (!value) return true; // Let required rule handle empty files
      // Handle both single File object and array of File objects
      if (value instanceof File) return maxCount >= 1 || content.file_max_count.replace('{max}', maxCount);
      if (Array.isArray(value)) return value.length <= maxCount || content.file_max_count.replace('{max}', maxCount);
      return true;
    },
    minFiles: (minCount) => (value) => {
      if (!value) return true; // Let required rule handle empty files
      // Handle both single File object and array of File objects
      if (value instanceof File) return minCount <= 1 || content.file_min_count.replace('{min}', minCount);
      if (Array.isArray(value)) return value.length >= minCount || content.file_min_count.replace('{min}', minCount);
      return true;
    },
  },
};

// functie om content bij te werken
export function setContent(newContent) {
  Object.assign(content, newContent);
}

export const content = {
  get required_field() { return i18n.global.t('form_rules.required_field'); },
  get is_number() { return i18n.global.t('form_rules.is_number'); },
  get is_year() { return i18n.global.t('form_rules.is_year'); },
  get is_positive() { return i18n.global.t('form_rules.is_positive'); },
  get is_email() { return i18n.global.t('form_rules.is_email'); },
  get is_phone_nl() { return i18n.global.t('form_rules.is_phone_nl'); },
  get is_phone() { return i18n.global.t('form_rules.is_phone'); },
  get is_link() { return i18n.global.t('form_rules.is_link'); },
  get select_required() { return i18n.global.t('form_rules.select_required'); },
  get min_items() { return i18n.global.t('form_rules.min_items'); },
  get price_end_rule() { return i18n.global.t('form_rules.price_end_rule'); },
  get max_length() { return i18n.global.t('form_rules.max_length'); },
  get min_length() { return i18n.global.t('form_rules.min_length'); },
  get checkbox_required() { return i18n.global.t('form_rules.checkbox_required'); },
  get invalid_date() { return i18n.global.t('form_rules.invalid_date'); },
  get min_date() { return i18n.global.t('form_rules.min_date'); },
  get max_date() { return i18n.global.t('form_rules.max_date'); },
  get past_date() { return i18n.global.t('form_rules.past_date'); },
  get adult_date() { return i18n.global.t('form_rules.adult_date'); },
  get file_required() { return i18n.global.t('form_rules.file_required'); },
  get file_max_size() { return i18n.global.t('form_rules.file_max_size'); },
  get file_allowed_types() { return i18n.global.t('form_rules.file_allowed_types'); },
  get file_max_count() { return i18n.global.t('form_rules.file_max_count'); },
  get file_min_count() { return i18n.global.t('form_rules.file_min_count'); },
  get min_hours_from_now() { return i18n.global.t('form_rules.min_hours_from_now'); },
  get min_duration_rule() { return i18n.global.t('form_rules.min_duration_rule'); },
  get max_duration_rule() { return i18n.global.t('form_rules.max_duration_rule'); },
};

// Helpers
function parseToDate(value) {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const intakeFormDate = parseIntakeFormDate(trimmed);
    if (intakeFormDate) return intakeFormDate;

    const parsed = new Date(trimmed);
    if (!isNaN(parsed.getTime())) return parsed;
  }
  // Fallback: attempt Date.parse
  const timestamp = Date.parse(value);
  if (!isNaN(timestamp)) return new Date(timestamp);
  return undefined;
}

function stripTime(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function formatYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
function combineEpochDateAndTime(dateEpoch, timeStr) {
  if (dateEpoch === null || dateEpoch === undefined || !timeStr) return undefined;

  const epochNum = Number(dateEpoch);
  if (Number.isNaN(epochNum)) return undefined;

  // detect seconds vs milliseconds
  const base = epochNum < 1e12 ? new Date(epochNum * 1000) : new Date(epochNum);

  const m = String(timeStr).trim().match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  if (!m) return undefined;

  const hours = Number(m[1]);
  const minutes = Number(m[2]);

  const dt = new Date(base);
  dt.setHours(hours, minutes, 0, 0); // local time
  return dt;
}
